import { Link, useLocation, useParams } from "react-router";
import { pollQuery } from "@/api/client";
import type { Query, QueryStatus } from "@/api/types";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";
import { usePolling } from "@/hooks/usePolling";
import { formatClock } from "@/lib/format";
import { Failure, Loading, to } from "@/site/parts";
import {
  MatchResult,
  NewResult,
  NoSignalResult,
  ProcessingResult,
  UncertainResult,
} from "@/site/outcomes";
import type { UploadNavState } from "@/site/upload";

export const STATUS: Record<
  QueryStatus,
  { title: string; word: string; tone: "signal" | "ink" | "off" }
> = {
  processing: { title: "Measuring", word: "Processing", tone: "signal" },
  match: { title: "Identified", word: "Match", tone: "signal" },
  uncertain: { title: "Inconclusive", word: "Inconclusive", tone: "ink" },
  new: { title: "New individual", word: "No match · new", tone: "signal" },
  no_shark: { title: "No signal", word: "No signal", tone: "off" },
  error: { title: "Fault", word: "Fault", tone: "off" },
};

export function formatStamp(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return `${d.toISOString().slice(0, 10)} ${d.toISOString().slice(11, 19)} UTC`;
}

function StatusBar({ id, query }: { id: string; query: Query | null }) {
  const status = STATUS[query?.status ?? "processing"];
  return (
    <div className="statusbar label" aria-live="polite">
      <span>
        Measurement{" "}
        <span style={{ color: "var(--ink)", textTransform: "none", letterSpacing: 0 }}>{id}</span>
      </span>
      <span>
        Status{" "}
        <span style={{ color: "var(--ink)" }}>
          <span
            className={`status-dot${query?.status === "processing" || !query ? " blink" : ""}`}
            data-tone={status.tone}
          />
          {status.word}
        </span>
      </span>
      {query && <span>Received {formatStamp(query.created_at)}</span>}
      {query?.recording && (
        <Link to={to.recording(query.recording.id)} className="link-u">
          ← Frame at {formatClock(query.recording.time)} of a recording
        </Link>
      )}
      <Link to={to.home} className="link-u" style={{ marginLeft: "auto" }}>
        New measurement →
      </Link>
    </div>
  );
}

export function Identify() {
  const { queryId = "" } = useParams();
  const location = useLocation();
  const preview = (location.state as UploadNavState | null)?.preview;
  const { data: query, error, retry } = usePolling(queryId, pollQuery);
  useDocumentMeta({ title: query ? STATUS[query.status].title : "Measuring" });

  let content: React.ReactNode;
  if (error && (!query || query.status === "processing")) {
    content = (
      <Failure error={error} title="The instrument lost this measurement." onRetry={retry} />
    );
  } else if (!query) {
    content = preview ? (
      <ProcessingResult imageUrl={preview} constellation={null} startedAt={null} />
    ) : (
      <Loading label="Locating measurement" />
    );
  } else {
    switch (query.status) {
      case "processing":
        content = (
          <ProcessingResult
            imageUrl={query.image_url}
            constellation={query.constellation}
            startedAt={query.created_at}
          />
        );
        break;
      case "match":
        content = <MatchResult query={query} />;
        break;
      case "uncertain":
        content = <UncertainResult query={query} />;
        break;
      case "new":
        content = <NewResult query={query} />;
        break;
      case "no_shark":
      case "error":
        content = <NoSignalResult query={query} />;
        break;
    }
  }
  return (
    <>
      <div className="in-wrap">
        <StatusBar id={queryId} query={query} />
      </div>
      {content}
    </>
  );
}
